import { supabase } from '@/lib/supabase'
import type { User } from '@/types'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

// Check if dev auth bypass is enabled
// CRITICAL: We enforce import.meta.env.DEV to ensure this NEVER happens in production
// even if the user forgets to clean up their .env file.
const isDevBypass = import.meta.env.DEV && import.meta.env.VITE_DEV_BYPASS_AUTH === 'true'

// Debug: log if dev bypass is active
if (isDevBypass) {
    console.log('🔓 VITE_DEV_BYPASS_AUTH=true detected (DEV MODE ONLY)')
} else if (import.meta.env.DEV) {
    console.log('💡 Tip: Set VITE_DEV_BYPASS_AUTH=true in .env.local to skip login')
}

export const useAuthStore = defineStore('auth', () => {
    const user = ref<User | null>(null)
    const isLoading = ref(false)
    const error = ref<string | null>(null)
    // Track if we've completed initial session check
    const initialized = ref(false)

    const isAuthenticated = computed(() => !!user.value)
    const isAdmin = computed(() => user.value?.isAdmin ?? false)

    // Dev bypass: set mock admin user
    function initDevUser() {
        if (isDevBypass) {
            console.log('🔓 Dev mode: bypassing authentication')
            user.value = {
                id: 'dev-user-id',
                email: 'dev@localhost',
                label: 'Dev User',
                isAdmin: true,
                created_at: new Date().toISOString()
            }
            initialized.value = true
            return true
        }
        return false
    }

    // Helper: wrap promise with timeout to prevent infinite hanging
    function withTimeout<T>(promise: Promise<T>, ms: number, errorMsg: string): Promise<T> {
        return Promise.race([
            promise,
            new Promise<T>((_, reject) =>
                setTimeout(() => reject(new Error(errorMsg)), ms)
            )
        ])
    }

    // Send OTP to email - checks whitelist first
    async function sendOtp(email: string): Promise<boolean> {
        isLoading.value = true
        error.value = null

        try {
            const normalizedEmail = email.toLowerCase().trim()

            // First check if email is in whitelist (with 10s timeout)
            const { data: whitelist, error: whitelistError } = await withTimeout(
                Promise.resolve(
                    supabase
                        .from('whitelist')
                        .select('id, email, is_active')
                        .eq('email', normalizedEmail)
                        .maybeSingle()
                ),
                10000,
                'Whitelist check timed out. Please try again.'
            )

            if (whitelistError) {
                if (import.meta.env.DEV) console.error('Whitelist check error:', whitelistError)
                // RLS might be blocking - try to give helpful message
                if (whitelistError.code === 'PGRST301' || whitelistError.message.includes('permission')) {
                    error.value = 'Database access error. Check RLS policies.'
                } else {
                    error.value = 'Failed to verify access'
                }
                return false
            }

            if (!whitelist) {
                error.value = 'Email not whitelisted. Contact admin for access.'
                return false
            }

            if (!whitelist.is_active) {
                error.value = 'Account is disabled. Contact admin for access.'
                return false
            }

            // Email is whitelisted, send magic link (with 15s timeout)
            const { error: otpError } = await withTimeout(
                supabase.auth.signInWithOtp({
                    email: normalizedEmail,
                    options: {
                        shouldCreateUser: true,
                        emailRedirectTo: `${window.location.origin}/hunt`
                    }
                }),
                15000,
                'Sending magic link timed out. Please try again.'
            )

            if (otpError) {
                if (import.meta.env.DEV) console.error('Magic link error:', otpError)
                error.value = otpError.message
                return false
            }

            return true
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Failed to send verification code'
            return false
        } finally {
            isLoading.value = false
        }
    }

    // Check existing session on app load (called after magic link redirect)
    // Resilient: does NOT sign out on transient errors (network, RLS)
    // Optimized: returns early if already initialized with valid user
    async function checkSession(force = false): Promise<boolean> {
        // Dev bypass: skip Supabase and use mock user
        if (isDevBypass) {
            return initDevUser()
        }

        // Skip if already initialized with a user (unless forced)
        if (!force && initialized.value && user.value) {
            return true
        }

        isLoading.value = true

        try {
            const { data: { session }, error: sessionError } = await withTimeout(
                supabase.auth.getSession(),
                5000,  // Reduced from 10s - fail fast on network issues
                'Session check timed out'
            )

            if (sessionError) {
                if (import.meta.env.DEV) console.warn('Session fetch error (keeping existing state):', sessionError)
                // Don't sign out on transient session errors - keep current user if any
                return !!user.value
            }

            if (!session?.user) {
                user.value = null
                initialized.value = true
                return false
            }

            // Check whitelist for existing session and get label + admin status (with 5s timeout - faster fail)
            // Use ilike for case-insensitive email matching (works with RLS)
            const userEmail = session.user.email?.toLowerCase() || ''
            const { data: whitelist, error: whitelistError } = await withTimeout(
                Promise.resolve(
                    supabase
                        .from('whitelist')
                        .select('label, is_active, is_admin, email')
                        .ilike('email', userEmail)
                        .eq('is_active', true)
                        .maybeSingle()
                ),
                5000,
                'Whitelist check timed out'
            )

            // Handle transient errors gracefully - keep session, don't sign out
            if (whitelistError) {
                if (import.meta.env.DEV) console.warn('Whitelist check failed (transient error, keeping session):', whitelistError)
                // If we already have a user from previous session, keep them
                if (user.value) return true

                // Optimistic login: Assume valid if we have a session, but limit access until verified
                user.value = {
                    id: session.user.id,
                    email: session.user.email,
                    isAdmin: false,
                    created_at: session.user.created_at || new Date().toISOString()
                }
                initialized.value = true
                return true
            }

            // Only sign out if whitelist query succeeded but user is not in it
            if (!whitelist) {
                error.value = 'Your email is not authorized or account is disabled. Contact admin.'
                await supabase.auth.signOut()
                user.value = null
                initialized.value = true
                return false
            }

            user.value = {
                id: session.user.id,
                email: session.user.email,
                label: whitelist.label || undefined,
                isAdmin: whitelist.is_admin ?? false,
                created_at: session.user.created_at || new Date().toISOString()
            }
            initialized.value = true

            return true
        } catch (err) {
            if (import.meta.env.DEV) console.error('Check session error (keeping existing state):', err)
            // On unexpected errors, keep current state
            return !!user.value
        } finally {
            isLoading.value = false
        }
    }

    // Handle magic link auth callback - extracts tokens from URL hash
    async function handleAuthCallback(): Promise<boolean> {
        isLoading.value = true
        error.value = null

        try {
            // Parse the hash to extract tokens
            const hash = window.location.hash.substring(1)
            const params = new URLSearchParams(hash)

            const accessToken = params.get('access_token')
            const refreshToken = params.get('refresh_token')

            // Check for error in URL (e.g., expired link)
            const urlError = params.get('error')
            const urlErrorDescription = params.get('error_description')
            if (urlError) {
                error.value = urlErrorDescription || urlError
                if (import.meta.env.DEV) console.error('Auth URL error:', urlError, urlErrorDescription)
                return false
            }

            if (!accessToken || !refreshToken) {
                error.value = 'Invalid login link. Please request a new one.'
                if (import.meta.env.DEV) console.error('Missing tokens in URL hash')
                return false
            }

            // Set the session with the tokens from the URL
            const { data, error: sessionError } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken
            })

            if (sessionError) {
                error.value = sessionError.message || 'Failed to establish session'
                if (import.meta.env.DEV) console.error('Failed to set session:', sessionError)
                return false
            }

            if (!data.session?.user) {
                error.value = 'Session could not be created. Please try again.'
                return false
            }

            // Now check whitelist and set user data
            const result = await checkSession(true)

            // If checkSession failed but didn't set an error, provide a generic one
            if (!result && !error.value) {
                error.value = 'Access verification failed. Please contact admin.'
            }

            return result
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Authentication failed'
            if (import.meta.env.DEV) console.error('Auth callback error:', err)
            return false
        } finally {
            isLoading.value = false
        }
    }

    // Setup auth state change listener for automatic session refresh
    function setupAuthListener() {
        supabase.auth.onAuthStateChange(async (event, session) => {
            if (event === 'SIGNED_OUT') {
                user.value = null
            } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
                // Re-check session to update user data
                if (session?.user && !user.value) {
                    await checkSession()
                }
            }
        })
    }

    // Logout
    async function logout() {
        await supabase.auth.signOut()
        user.value = null
        error.value = null
    }

    // Clear error
    function clearError() {
        error.value = null
    }

    return {
        user,
        isLoading,
        isAuthenticated,
        isAdmin,
        initialized,
        error,
        sendOtp,
        checkSession,
        handleAuthCallback,
        setupAuthListener,
        logout,
        clearError,
        initDevUser,
        isDevBypass
    }
})
